import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import StoreIcon from "@mui/icons-material/Store";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  writeBatch,
  doc,
} from "firebase/firestore";
import BottomNav from "./BottomNav";

const timeAgo = (ts) => {
  if (!ts) return "";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
};

const typeConfig = {
  message: { icon: <ChatIcon sx={{ fontSize: 18 }} />, color: "#1976d2", label: "Mensaje" },
  product: { icon: <StoreIcon sx={{ fontSize: 18 }} />, color: "#7c3aed", label: "Publicación" },
};

const Notifications = () => {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(
      collection(db, "notifications"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      setNotifs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return unsub;
  }, []);

  const markAllRead = async () => {
    const user = auth.currentUser;
    if (!user) return;
    const unread = notifs.filter((n) => !n.read);
    if (!unread.length) return;
    const batch = writeBatch(db);
    unread.forEach((n) => batch.update(doc(db, "notifications", n.id), { read: true }));
    await batch.commit();
  };

  const handleClick = async (notif) => {
    // Mark as read
    const batch = writeBatch(db);
    batch.update(doc(db, "notifications", notif.id), { read: true });
    await batch.commit();

    // Navigate
    if (notif.type === "message" && notif.relatedId) {
      const user = auth.currentUser;
      navigate(`/direct-chat/${notif.relatedId}/${notif.fromUid}/${user.uid}`);
    }
  };

  const unreadCount = notifs.filter((n) => !n.read).length;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f6fa", pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1976d2 0%, #7c3aed 100%)",
          pt: 6, pb: 3, px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: "#fff", p: 0.5 }} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
            Notificaciones
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                size="small"
                sx={{ ml: 1, bgcolor: "#ef4444", color: "#fff", fontWeight: 700, height: 20, fontSize: 11 }}
              />
            )}
          </Typography>
        </Box>
        {unreadCount > 0 && (
          <IconButton onClick={markAllRead} sx={{ color: "rgba(255,255,255,0.85)" }} size="small">
            <DoneAllIcon sx={{ fontSize: 22 }} />
          </IconButton>
        )}
      </Box>

      <Box sx={{ px: 2, mt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
            <CircularProgress />
          </Box>
        ) : notifs.length === 0 ? (
          <Box sx={{ textAlign: "center", pt: 8, color: "#94a3b8" }}>
            <NotificationsNoneIcon sx={{ fontSize: 56, mb: 1, opacity: 0.4 }} />
            <Typography variant="body1">Sin notificaciones</Typography>
          </Box>
        ) : (
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            {notifs.map((notif, idx) => {
              const cfg = typeConfig[notif.type] || typeConfig.message;
              return (
                <React.Fragment key={notif.id}>
                  <Box
                    onClick={() => handleClick(notif)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                      cursor: "pointer",
                      bgcolor: notif.read ? "#fff" : "#f0f4ff",
                      transition: "background 0.15s",
                      "&:hover": { bgcolor: "#f8faff" },
                      position: "relative",
                    }}
                  >
                    {/* Avatar + tipo icono */}
                    <Box sx={{ position: "relative", flexShrink: 0 }}>
                      <Avatar
                        src={notif.fromImage}
                        sx={{ width: 46, height: 46, bgcolor: "#e2e8f0" }}
                      >
                        {!notif.fromImage && (notif.fromName?.[0] || "?")}
                      </Avatar>
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: -2,
                          right: -2,
                          bgcolor: cfg.color,
                          borderRadius: "50%",
                          width: 20,
                          height: 20,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          border: "2px solid #fff",
                        }}
                      >
                        {cfg.icon}
                      </Box>
                    </Box>

                    {/* Texto */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: notif.read ? 400 : 700, color: "#1e293b", lineHeight: 1.4 }}>
                        <span style={{ fontWeight: 700 }}>{notif.fromName}</span>{" "}
                        {notif.text}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: notif.read ? "#94a3b8" : cfg.color, mt: 0.3, fontWeight: notif.read ? 400 : 600 }}>
                        {timeAgo(notif.createdAt)}
                      </Typography>
                    </Box>

                    {/* Punto no leído */}
                    {!notif.read && (
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#1976d2", flexShrink: 0, mt: 1 }} />
                    )}
                  </Box>
                  {idx < notifs.length - 1 && <Divider sx={{ ml: 8 }} />}
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Box>

      <BottomNav />
    </Box>
  );
};

export default Notifications;
